import React, { createContext, useContext } from 'react'
import { useQuery, ApolloError } from '@apollo/client'
import { getAssociationPublicQuery } from '../queries/queries'
import { getTeamQuery } from '../queries/team'
import { getDashboardAssociationBookletsQuery } from '../queries/booklet'
import { Association, Team, Booklet } from '../types/generated.types'

interface DashboardTeamContextType {
  associationId: string
  teamId: string
  associationData: Association | undefined
  teamData: Team | undefined
  associationBooklets: Booklet[]
  shopShareDomain: string | undefined
  defaultBooklet: Booklet | undefined
  refetchTeam: () => void
  loading: boolean
  error: ApolloError | undefined
}

const DashboardTeamContext = createContext<DashboardTeamContextType | undefined>(undefined)

interface DashboardTeamProviderProps {
  associationId: string
  teamId: string
  children: React.ReactNode
}

export const DashboardTeamProvider: React.FC<DashboardTeamProviderProps> = ({
  associationId,
  teamId,
  children
}) => {
  const { data: associationQueryData, loading: associationLoading, error: associationError } = useQuery(getAssociationPublicQuery, { variables: { associationId } })
  const { data: teamQueryData, loading: teamLoading, error: teamError, refetch: refetchTeam } = useQuery(getTeamQuery, { variables: { teamId } })
  const { data: dashboardBookletsData, loading: bookletsLoading, error: bookletsError } = useQuery(getDashboardAssociationBookletsQuery, { variables: { associationId, teamId } })

  const associationData = associationQueryData?.association
  const teamData = teamQueryData?.team
  const associationBooklets = dashboardBookletsData?.booklets || []

  const shopShareDomain = associationData?.shopShareDomain?.startsWith('http')
    ? associationData.shopShareDomain
    : `https://${associationData?.shopShareDomain}`;

  // Default booklet is the booklet with the latest validFrom date
  const defaultBooklet = associationBooklets
    .filter((booklet) => booklet.validFrom)
    .sort((a, b) => new Date(b.validFrom).getTime() - new Date(a.validFrom).getTime())[0]

  const loading = associationLoading || teamLoading || bookletsLoading
  const error = associationError || teamError || bookletsError

  const contextValue: DashboardTeamContextType = {
    associationId,
    teamId,
    associationData,
    teamData,
    associationBooklets,
    shopShareDomain,
    defaultBooklet,
    refetchTeam,
    loading,
    error
  }

  return (
    <DashboardTeamContext.Provider value={contextValue}>
      {children}
    </DashboardTeamContext.Provider>
  )
}

export const useDashboardTeamContext = (): DashboardTeamContextType => {
  const context = useContext(DashboardTeamContext)
  if (context === undefined) {
    throw new Error('useDashboardTeamContext must be used within a DashboardTeamProvider')
  }
  return context
}
